import { Fragment, useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { alsGroesse, Dokumente } from '../komponenten/Dokumente'

interface AnhangAntwort {
  id: number
  art: string
  dateiname: string
  mimetyp: string | null
  groesseBytes: number
}

interface DateiAntwort {
  id: number
  dateiname: string
  groesseBytes: number
  status: string
  hochgeladenAm: string
  anzahlKurse: number
  anzahlHinweise: number
  protokoll: string | null
  speicherart: string
  savename: string | null
  anhaenge: AnhangAntwort[]
}

interface ImportAntwort {
  dateiId: number
  dateiname: string
  anzahlKurse: number
  status: string
  bereitsVorhanden: boolean
  protokoll: string[]
}

const STATUS_ETIKETT: Record<string, string> = {
  IMPORTIERT: 'gruen',
  MIT_HINWEISEN: 'orange',
  FEHLER: 'rot',
  NEU: 'grau',
}

const STATUS_TEXT: Record<string, string> = {
  IMPORTIERT: 'importiert',
  MIT_HINWEISEN: 'mit Hinweisen',
  FEHLER: 'Fehler',
  NEU: 'neu',
}

const SPEICHER_TEXT: Record<string, string> = {
  LOKAL: 'Server',
  S3: 'S3',
  DATENBANK: 'Datenbank',
}

/** Welcher Bereich einer Zeile aufgeklappt ist — beides zugleich wäre unübersichtlich. */
type Aufgeklappt = { id: number; was: 'protokoll' | 'anhaenge' } | null

export function Bibliothek() {
  const { hatRolle } = useAuth()
  const [dateien, setDateien] = useState<DateiAntwort[]>([])
  const [laedt, setLaedt] = useState(true)
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)
  const [offen, setOffen] = useState<Aufgeklappt>(null)
  const dateiFeld = useRef<HTMLInputElement>(null)

  const darfHochladen = hatRolle('ADMIN', 'DISPONENT')

  async function laden() {
    setLaedt(true)
    try {
      setDateien(await api.get<DateiAntwort[]>('/api/kursbibliothek'))
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    } finally {
      setLaedt(false)
    }
  }

  useEffect(() => {
    void laden()
  }, [])

  async function hochladen(dateiliste: FileList | null) {
    if (!dateiliste || dateiliste.length === 0) return
    setMeldung(null)

    const ergebnisse: string[] = []
    for (const datei of Array.from(dateiliste)) {
      try {
        const antwort = await api.hochladen<ImportAntwort>('/api/kursbibliothek', datei)
        ergebnisse.push(
          antwort.bereitsVorhanden
            ? `${antwort.dateiname}: war bereits in der Bibliothek`
            : `${antwort.dateiname}: ${antwort.anzahlKurse} Kurs(e) eingelesen`,
        )
      } catch (ex) {
        ergebnisse.push(`${datei.name}: ${ex instanceof Error ? ex.message : 'Fehler'}`)
      }
    }

    setMeldung({ art: 'erfolg', text: ergebnisse.join(' · ') })
    if (dateiFeld.current) dateiFeld.current.value = ''
    await laden()
  }

  async function loeschen(id: number, name: string) {
    if (!confirm(`„${name}" mitsamt der daraus erzeugten Kurse löschen?`)) return
    try {
      await api.loeschen(`/api/kursbibliothek/${id}`)
      await laden()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Löschen fehlgeschlagen' })
    }
  }

  async function herunterladen(datei: DateiAntwort) {
    try {
      await api.herunterladen(`/api/kursbibliothek/${datei.id}/datei`, datei.dateiname)
    } catch (ex) {
      setMeldung({
        art: 'fehler',
        text: ex instanceof Error ? ex.message : 'Herunterladen fehlgeschlagen',
      })
    }
  }

  function umschalten(id: number, was: 'protokoll' | 'anhaenge') {
    setOffen(offen?.id === id && offen.was === was ? null : { id, was })
  }

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Kursbibliothek</h1>
          <p>
            Hochgeladene Kursblätter mit ihren Unterlagen. Die Originaldateien bleiben erhalten.
          </p>
        </div>
        {darfHochladen && (
          <div>
            <input
              ref={dateiFeld}
              type="file"
              accept=".xls,.xlsx"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => void hochladen(e.target.files)}
            />
            <button className="knopf-akzent" onClick={() => dateiFeld.current?.click()}>
              Kursblätter hochladen
            </button>
          </div>
        )}
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      <div className="tabelle-huelle">
        {laedt ? (
          <div className="laden">Wird geladen …</div>
        ) : dateien.length === 0 ? (
          <div className="leer">
            Noch keine Kursblätter vorhanden.
            {darfHochladen && ' Laden Sie Excel-Dateien im Format .xls oder .xlsx hoch.'}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Datei</th>
                <th>Größe</th>
                <th>Ablage</th>
                <th>Kurse</th>
                <th>Status</th>
                <th>Hinweise</th>
                <th>Unterlagen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dateien.map((datei) => (
                <Fragment key={datei.id}>
                  <tr>
                    <td>
                      <strong>{datei.dateiname}</strong>
                    </td>
                    <td className="zahl">{alsGroesse(datei.groesseBytes)}</td>
                    <td>
                      <span className="etikett grau">
                        {SPEICHER_TEXT[datei.speicherart] ?? datei.speicherart}
                      </span>
                      {datei.savename && (
                        <div
                          style={{
                            color: 'var(--grau-500)',
                            fontSize: '0.75rem',
                            fontFamily: 'monospace',
                            marginTop: 2,
                          }}
                        >
                          {datei.savename}
                        </div>
                      )}
                    </td>
                    <td className="zahl">{datei.anzahlKurse}</td>
                    <td>
                      <span className={`etikett ${STATUS_ETIKETT[datei.status] ?? 'grau'}`}>
                        {STATUS_TEXT[datei.status] ?? datei.status}
                      </span>
                    </td>
                    <td>
                      {datei.anzahlHinweise > 0 ? (
                        <button
                          className="knopf-schlicht"
                          style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                          onClick={() => umschalten(datei.id, 'protokoll')}
                        >
                          {datei.anzahlHinweise} anzeigen
                        </button>
                      ) : (
                        <span style={{ color: 'var(--grau-500)' }}>–</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="knopf-schlicht"
                        style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                        onClick={() => umschalten(datei.id, 'anhaenge')}
                      >
                        {datei.anhaenge.length > 0
                          ? `${datei.anhaenge.length} Datei(en)`
                          : 'hinterlegen'}
                      </button>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        className="knopf-schlicht"
                        style={{ padding: '5px 12px' }}
                        onClick={() => void herunterladen(datei)}
                      >
                        Herunterladen
                      </button>{' '}
                      {hatRolle('ADMIN') && (
                        <button
                          className="knopf-schlicht"
                          style={{ padding: '5px 12px', color: 'var(--rot-500)' }}
                          onClick={() => void loeschen(datei.id, datei.dateiname)}
                        >
                          Löschen
                        </button>
                      )}
                    </td>
                  </tr>

                  {offen?.id === datei.id && offen.was === 'protokoll' && datei.protokoll && (
                    <tr>
                      <td colSpan={8} style={{ background: 'var(--grau-50)' }}>
                        <div className="protokoll">{datei.protokoll}</div>
                      </td>
                    </tr>
                  )}

                  {offen?.id === datei.id && offen.was === 'anhaenge' && (
                    <tr>
                      <td colSpan={8} style={{ background: 'var(--grau-50)' }}>
                        <Dokumente
                          kompakt
                          bezugsart="KURSBLATTDATEI"
                          bezugId={datei.id}
                          hinweis="Der Fahrplan des Bestellers und alles Weitere zu diesem Kursblatt. Was hier liegt, erscheint auch bei jedem Kurs daraus."
                          beiAenderung={() => void laden()}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Unterlagen ohne Bezug zu einem einzelnen Kursblatt: der Fahrplan des Bestellers
          fuer den ganzen Fahrplanzeitraum, Umlaufplaene, Schriftverkehr. */}
      <Dokumente
        bezugsart="MANDANT"
        titel="Unterlagen des Betriebs"
        hinweis="Fahrpläne und Umlaufpläne, die für den ganzen Fahrplanzeitraum gelten und nicht an einem einzelnen Kursblatt hängen."
      />
    </>
  )
}
